import React, { useEffect, useState, useRef, useCallback } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLang } from '../../context/LangContext';
import { parseApiDateTime } from '../../utils/dateUtils';
import { useUserAuth } from '../../context/UserAuthContext';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { ChipItem } from '../../components/common/FilterChips';
import NoDataContainer from '../../components/NoDataContainer';
import PrimaryButton from '../../components/PrimaryButton';
import Modal from '../../components/Modal';
import { AppDropdownField } from '../../customComponents/AppDropdownField';
import AuctionHouseComponent from '../../components/common/AuctionHouse';
import ImageView from '../../components/ImageView';
import LiveBadge from '../../customComponents/LiveBadge';
import FavoriteIcon from '../../components/common/FavoriteIcon';
import { formatLiveTime, formatAuctionPeriod } from '../../utils/dateUtils';
import { navigateWithScrollTop } from '../../utils/navigationScroll';
import { searchLots } from '../../lib/api/user/userApi';
import { SearchLotsParams } from '../../lib/api/user/userApi.types';
import { MAJOR_CATEGORY_OPTIONS } from '../../constants/categories';
import { isShoppingMode } from '../../config/appMode';

// 스타일드 컴포넌트들
const SearchPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SearchResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 16px 0px;
  min-height: 400px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px;
    padding: 32px 0;
    min-height: 500px;
  }
`;

// ListSection의 AuctionCard 스타일 추가
const AuctionCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  position: relative;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-4px);
  }
`;

// 벳지 컨테이너 (이미지 위에 배치)
const BadgeContainer = styled.div`
  width: auto;
  margin-bottom: 8px;
  display: flex;
  justify-content: flex-start;
`;

const CardImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  position: relative;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
  padding: 16px;
  box-sizing: border-box;
`;

// 이미지 오버레이 (하단 절반을 차지)
const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 12px 16px;
  box-sizing: border-box;
  border-radius: 8px;
`;

// 종료된 경매용 전체 이미지 오버레이 (반투명)
const EndedAuctionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EndedAuctionText = styled.span`
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.5px;
`;

// 날짜 정보 (오버레이 내부) - 모바일용
const OverlayDateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #ffffff;
  line-height: 1.3;
  margin-bottom: 4px;
`;

const OverlayDateLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
`;

const OverlayDateValue = styled.span`
  font-size: 12px;
  color: #ffffff;
  font-weight: 500;
`;

// 날짜 정보 (오버레이 내부) - 웹용
const DesktopOverlayDateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #ffffff;
  line-height: 1.3;
  margin-bottom: 6px;
`;

const DesktopOverlayDateLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
`;

const DesktopOverlayDateValue = styled.span`
  font-size: 14px;
  color: #ffffff;
  font-weight: 500;
`;

const CardContent = styled.div`
  padding: 16px;
  text-align: left;
  min-width: 0;
  box-sizing: border-box;
`;

const AuctionTitle = styled.div`
  font-size: 20px;
  color: #000;
  font-weight: 600;
  margin-bottom: 8px;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  line-height: 1.4;
`;

const LotName = styled.h3`
  font-size: 16px;
  color: #000;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`;

const PriceInfo = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceLabel = styled.span`
  font-size: 12px;
  color: #999;
  font-weight: 400;
`;

const PriceValue = styled.span`
  font-size: 14px;
  color: #000;
  font-weight: 600;
`;

// 모바일 전용 검색 입력창 컨테이너
const MobileSearchContainer = styled.div`
  padding: 16px 0px 16px 0px; /* 상단 여백을 더 줄임 */
  background-color: #fff;
  /* border-bottom: 1px solid #e5e5e5; */

  @media (min-width: 768px) {
    display: none; /* 웹에서는 숨김 */
  }
`;

const MobileSearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const MobileSearchInputField = styled.input`
  width: 100%;
  height: 36px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0 36px 0 12px;
  font-size: 16px;
  outline: none;
  box-sizing: border-box;
  background-color: #fff;
  cursor: pointer;
  
  &::placeholder {
    color: #6c757d;
  }
  
  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    cursor: text;
  }
`;

const MobileSearchIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    background: url('/common/search.svg') no-repeat center;
    background-size: contain;
  }
  
  &:hover::before {
    opacity: 0.7;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  @media (min-width: 768px) {
    gap: 16px;
    margin-bottom: 24px;
  }
`;

const AuctionStatusDropdownContainer = styled.div`
  flex-shrink: 0;
  width: 120px;
`;

// 이제 FilterContainer 자체에서 모든 아이템들이 flex-wrap 됩니다
const FilterChipsContainer = styled.div`
  display: contents; /* 컨테이너를 투명하게 만들어 내부 요소들이 직접 FilterContainer의 자식이 되도록 */
`;

// 커스텀 칩 버튼 스타일
const CustomChipButton = styled.button<{ $isActive: boolean }>`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  height: 40px; /* 드롭다운과 같은 높이 */
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* 첫 번째 칩이 드롭다운과 같은 행에 있을 때의 최소 너비 설정 */
  min-width: fit-content;
  
  ${({ $isActive }) => 
    $isActive 
      ? `
        background: #262626;
        color: #ffffff;
        border: 1px solid #262626;
      `
      : `
        background: #ffffff;
        color: #666666;
        border: 1px solid #cccccc;
        
        &:hover {
          border-color: #999999;
        }
      `
  }
  
  @media (min-width: 768px) {
    font-size: 15px;
  }
`;

// 더보기 버튼 스타일
const MoreButton = styled.button<{ $isExpanded: boolean }>`
  padding: 0;
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background: #ffffff;
  border: none; /* 보더 제거 */
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E") no-repeat center;
    background-size: contain;
    transform: ${({ $isExpanded }) => $isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.2s ease;
  }
`;

const SearchResultsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
  padding: 0 16px;

  @media (min-width: 768px) {
    padding: 0;
    margin-bottom: 24px;
  }
`;

const SearchQuery = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const SearchCount = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

// FormTitle 스타일 추가
const FormTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #262626;
  margin: 0;
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

// 검색 결과 아이템 인터페이스 - 실제 API 응답에 맞게 수정
interface SearchResultItem {
  index: number;
  lot_no: number;
  code: string;
  name: string;
  description: string;
  start_price: number;
  exchange_rate: number;
  start_price_usd: number;
  recommend_no: number;
  images: string[];
  disabled: boolean;
  ended: boolean;
  create_at: string;
  update_at: string;
  create_by: string;
  update_by: string;
  category: {
    code: string;
    name: string;
    krName: string;
  };
  sub_category: {
    code: string;
    name: string;
    krName: string;
  };
  auctioneer: {
    index: number;
    name: string;
    code: string;
    description: string;
    profilePic: string;
    term: string;
  };
  auction: {
    index: number;
    name: string;
    description: string;
    code: string;
    url: string;
    auctionLotNo: number;
    preBidStartAt: string;
    preBidEndAt: string;
    auctionStartAt: string;
    auctionEndAt: string;
    paymentDueAt: string;
    auctionFeeRate: number;
    platformFeeRate: number;
    createAt: string;
    updateAt: string | null;
    biddingRanges: Array<{
      index: number;
      minBidAmount: number;
      maxBidAmount: number | null;
      rate: number;
    }>;
  } | null;
  like_count: string;
  liked_user_indexes: number[];
  is_liked: boolean;
  max_pre_bid_price: number;
}

// 필터 칩 옵션들 - MAJOR_CATEGORY_OPTIONS 사용 (ListSection과 동일)
const getFilterChips = (language: 'ko' | 'en'): ChipItem[] => {
  const allChip = { key: 'all', label: language === 'ko' ? '전체' : 'All' };
  
  const categoryChips = MAJOR_CATEGORY_OPTIONS.map(option => ({
    key: option.value,
    label: language === 'ko' ? option.label : (option.labelEn || option.label)
  }));

  return [allChip, ...categoryChips];
};

// 경매 상태 드롭다운 옵션들
const getAuctionStatusOptions = (language: 'ko' | 'en') => {
  const statusTexts = {
    ko: {
      all: '전체 경매',
      upcoming: '예정 경매', 
      ongoing: '진행 경매',
      ended: '종료 경매'
    },
    en: {
      all: 'All Auctions',
      upcoming: 'Upcoming Auctions',
      ongoing: 'Ongoing Auctions', 
      ended: 'Ended Auctions'
    }
  };

  const texts = statusTexts[language];
  
  return [
    { value: 'all', label: texts.all },
    { value: 'upcoming', label: texts.upcoming },
    { value: 'ongoing', label: texts.ongoing },
    { value: 'ended', label: texts.ended }
  ];
};

// 다국어 텍스트
const getSearchTexts = (lang: 'ko' | 'en') => {
  const shopping = isShoppingMode();
  
  if (lang === 'en') {
    return {
      title: 'Search Results',
      searchResults: 'Search Results',
      resultsCount: ' results',
      noResults: 'No search results found',
      noResultsDescription: 'Try searching with different keywords.',
      noSearchPrompt: 'Enter search terms or select a category',
      searchPlaceholder: shopping ? 'Search by seller, product name' : 'Search by auction house, auction, lot name',
      bidButton: 'Bid',
      preSubmission: 'Pre-Bid',
      liveView: 'Live View',
      liveBid: 'Live Bid',
      auctionEnded: shopping ? 'Sale Ended' : 'Auction Ended',
      auctionNotStarted: shopping ? 'Sale Not Started' : 'Auction Not Started',
      auctionEndedTitle: shopping ? 'Sale Ended' : 'Auction Ended',
      auctionEndedMessage: shopping ? 'This sale has already ended.' : 'This auction has already ended.',
      confirmButton: 'OK',
      liveStartPrice: 'Live Starting Price',
      startPrice: shopping ? 'Price' : 'Starting Price',
      auctionStart: shopping ? 'Sale Start' : 'Auction Start',
      auctionEnd: shopping ? 'Sale End' : 'Auction End',
      preBid: 'Pre-Bid',
      maxPreBidPrice: 'Highest Pre-Bid'
    };
  }
  
  return {
    title: '검색결과',
    searchResults: '검색 결과',
    resultsCount: '건의 결과',
    noResults: '검색 결과가 없습니다',
    noResultsDescription: '다른 검색어로 시도해보세요.',
    noSearchPrompt: '검색어를 입력하거나 카테고리를 선택해주세요',
    searchPlaceholder: shopping ? '판매자, 상품명으로 검색' : '경매사, 경매, lot명으로 검색',
    bidButton: '입찰하기',
    preSubmission: '사전 입찰하기',
    liveView: '라이브참여하기',
    liveBid: '경매등록하기',
    auctionEnded: shopping ? '판매종료' : '경매종료',
    auctionNotStarted: shopping ? '판매 시작 전입니다' : '경매 시작 전입니다',
    auctionEndedTitle: shopping ? '판매가 종료되었습니다' : '경매가 종료되었습니다',
    auctionEndedMessage: shopping ? '이 판매는 이미 종료되었습니다.' : '이 경매는 이미 종료되었습니다.',
    confirmButton: '확인',
    liveStartPrice: '라이브 시작가',
    startPrice: shopping ? '판매가' : '시작가',
    auctionStart: shopping ? '판매 시작' : '경매 시작',
    auctionEnd: shopping ? '판매 종료' : '경매 종료',
    preBid: '사전입찰',
    maxPreBidPrice: '최고 사전입찰가'
  };
};

// 요일 다국어 텍스트
const dayNamesLocalized = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
};

// 이미지 URL 처리 함수 (현재 사용하지 않음 - 향후 구현 예정)
// const getProfileImageSrc = (profileImageUrl?: string | null) => {
//   if (!profileImageUrl || profileImageUrl.trim() === "") {
//     return "/common/nodata.svg";
//   }
  
//   if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
//     return profileImageUrl;
//   }
  
//   if (profileImageUrl.startsWith("/")) {
//     return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
//   }
  
//   return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
// };

// LiveEndTime 포맷팅 함수 (현재 사용하지 않음 - 향후 구현 예정)
// const formatLiveEndTime = (dateStr: string, language: 'ko' | 'en' = 'ko'): string => {
//   try {
//     const date = new Date(dateStr);
//     const month = (date.getMonth() + 1).toString().padStart(2, '0');
//     const day = date.getDate();
//     const hours = date.getHours().toString().padStart(2, '0');
//     const minutes = date.getMinutes().toString().padStart(2, '0');
//     const dayName = dayNamesLocalized[language][date.getDay()];
    
//     if (language === 'ko') {
//       return `${month}/${day}(${dayName}) ${hours}:${minutes} 종료`;
//     } else {
//       const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
//                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
//       const monthName = monthNames[date.getMonth()];
//       return `${monthName} ${day}(${dayName}) ${hours}:${minutes} Ends`;
//     }
//   } catch (error) {
//     return dateStr;
//   }
// };

// 날짜를 로컬 형식으로 포맷팅하는 함수
const formatDateToLocalString = (date: Date, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const dayName = dayNamesLocalized[language][date.getDay()];
    
    return language === 'ko' 
      ? `${year}.${month}.${day} (${dayName}) ${hours}:${minutes}`
      : `${hours}:${minutes} on ${month}/${day}/${year} (${dayName})`;
  } catch (error) {
    return date.toString();
  }
};

// KRW 가격 포맷팅 함수 (안전하게 처리)
const formatPrice = (
  price: number | null | undefined,
  language: 'ko' | 'en' = 'ko',
  exchangeRate: number = 1300
): string => {
  if (typeof price !== 'number' || Number.isNaN(price)) {
    // 잘못된 값이 들어오면 표시만 "-"로 처리하여 렌더는 계속되도록 함
    return '-';
  }

  if (language === 'ko') {
    return `KRW ${price.toLocaleString()}`;
  }

  const safeRate = !exchangeRate || Number.isNaN(exchangeRate) ? 1300 : exchangeRate;
  const usdPrice = price / safeRate;
  return `USD ${usdPrice.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

// 경매 상태 확인 함수
const getAuctionStatus = (item: SearchResultItem): 'before-submission' | 'submission-period' | 'before-live' | 'live' | 'ended' => {
  const now = new Date();
  
  const auction = item.auction;
  if (!auction) return 'ended';
  
  const preBidStartAt = parseApiDateTime(auction.preBidStartAt);
  const preBidEndAt = parseApiDateTime(auction.preBidEndAt);
  const auctionStartAt = parseApiDateTime(auction.auctionStartAt);
  const auctionEndAt = parseApiDateTime(auction.auctionEndAt);

  if (!preBidStartAt || !preBidEndAt || !auctionStartAt || !auctionEndAt) return 'ended';
  
  if (now > auctionEndAt) return 'ended';
  if (now < preBidStartAt) return 'before-submission';
  if (now >= preBidStartAt && now < preBidEndAt) return 'submission-period';
  if (now >= preBidEndAt && now < auctionStartAt) return 'before-live';
  return 'live';
};

// API 데이터를 SearchResultItem 형태로 변환하는 함수 - 실제 응답에 맞게 수정
const convertApiDataToSearchResultItem = (apiData: any): SearchResultItem => {
  return {
    index: apiData.index,
    lot_no: apiData.lotNo,
    code: apiData.code,
    name: apiData.name,
    description: apiData.description,
    start_price: apiData.startPrice,
    exchange_rate: apiData.exchangeRate,
    start_price_usd: apiData.startPriceUsd,
    recommend_no: apiData.recommendNo,
    images: apiData.images || [],
    disabled: apiData.disabled,
    ended: apiData.ended,
    create_at: apiData.createAt,
    update_at: apiData.updateAt,
    create_by: apiData.createBy,
    update_by: apiData.updateBy,
    category: apiData.category,
    sub_category: apiData.subCategory,
    auctioneer: apiData.auctioneer,
    auction: apiData.auction,
    like_count: apiData.likeCount,
    liked_user_indexes: apiData.likedUserIndexes || [],
    is_liked: apiData.isLiked,
    max_pre_bid_price: apiData.maxPreBidPrice
  };
};

function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useUserAuth(); // 사용자 인증 정보
  const { lang } = useLang();
  const texts = getSearchTexts(lang);

  // 상태 관리
  const [isLoading, setIsLoading] = useState(true);
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [selectedChips, setSelectedChips] = useState<string[]>(['all']);
  const [selectedAuctionStatus, setSelectedAuctionStatus] = useState<string>('all'); // 경매 상태 드롭다운 상태
  const [isChipsExpanded, setIsChipsExpanded] = useState(false); // 칩스 확장/축소 상태
  const [maxVisibleChips, setMaxVisibleChips] = useState<number>(999); // 첫 줄에 표시 가능한 최대 칩 개수
  
  // 컨테이너 참조
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const [mobileSearchQuery, setMobileSearchQuery] = useState(''); // 모바일 검색어 상태
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    buttonText: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    buttonText: ''
  });

  // 검색어 가져오기
  const query = searchParams.get('q') || '';

  // URL의 query가 변경되면 모바일 검색어 동기화
  useEffect(() => {
    setMobileSearchQuery(query);
  }, [query]);

  // 검색 데이터 로드
  useEffect(() => {
    const loadSearchResults = async () => {
      setIsLoading(true);
      
      // 검색어나 필터가 있을 때만 검색 수행
      const shouldSearch = query || selectedChips.some(chip => chip !== 'all') || selectedAuctionStatus !== 'all';
      
      if (!shouldSearch) {
        setSearchResults([]);
        setIsLoading(false);
        return;
      }
      
      try {
        // API 파라미터 구성
        const apiParams: SearchLotsParams = {
          page: 1,
          limit: 30
        };

        // 검색 키워드 추가 (서버에서 키워드 검색 처리)
        if (query && query.trim() !== '') {
          apiParams.keyword = query.trim();
        }

        // 카테고리 필터 추가
        if (!selectedChips.includes('all') && selectedChips.length > 0) {
          // 첫 번째 선택된 카테고리만 전달 (API는 단일 카테고리만 지원)
          apiParams.categoryCode = selectedChips[0];
        }

        // 경매 상태 필터 추가
        if (selectedAuctionStatus !== 'all') {
          apiParams.auctionStatus = selectedAuctionStatus as 'ongoing' | 'upcoming' | 'ended';
        }

        // 사용자 정보 추가 (로그인된 경우)
        if (user?.index) {
          apiParams.user = user.index;
        }

        devLog('Search API 호출 파라미터:', apiParams);

        // API 호출
        const response = await searchLots(apiParams);
        
        devLog('Search API 응답:', response);

        if (response && response.data && Array.isArray(response.data.data)) {
          const results = response.data.data.map(convertApiDataToSearchResultItem);
          setSearchResults(results);
        } else {
          devWarn('예상과 다른 API 응답 구조:', response);
          setSearchResults([]);
        }
      } catch (error) {
        devError('검색 결과 로드 중 오류:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadSearchResults();
  }, [query, selectedChips, selectedAuctionStatus, user?.index]); // user.index도 의존성에 추가

  // 필터 칩 클릭 핸들러
  const handleChipClick = (chipKey: string) => {
    if (chipKey === 'all') {
      setSelectedChips(['all']);
    } else {
      const newSelectedChips = selectedChips.includes(chipKey)
        ? selectedChips.filter(key => key !== chipKey && key !== 'all')
        : [...selectedChips.filter(key => key !== 'all'), chipKey];
      
      setSelectedChips(newSelectedChips.length === 0 ? ['all'] : newSelectedChips);
    }
  };

  // 경매 상태 드롭다운 변경 핸들러
  const handleAuctionStatusChange = (value: string) => {
    setSelectedAuctionStatus(value);
  };

  // 칩스 확장/축소 토글 함수
  const toggleChipsExpansion = () => {
    setIsChipsExpanded(!isChipsExpanded);
  };

  // 첫 줄에 들어갈 수 있는 최대 칩 개수 계산
  const calculateMaxVisibleChips = useCallback(() => {
    // 웹일 때는 고정값 사용 (1200px 고정 너비이므로)
    if (window.innerWidth >= 768) {
      return 9; // 웹에서는 9개까지만 표시
    }
    
    // 모바일일 때만 동적 계산
    if (!filterContainerRef.current) return 999;
    
    const container = filterContainerRef.current;
    const containerWidth = container.offsetWidth;
    const dropdownWidth = 120; // 드롭다운 너비
    const gap = 12; // 모바일 갭
    const moreButtonWidth = 40; // 더보기 버튼 너비
    
    // 드롭다운 + 갭 + 더보기버튼 + 여유공간(갭) 제외한 나머지
    const reservedWidth = dropdownWidth + gap + moreButtonWidth + gap; // 더보기 버튼 앞뒤 여유공간
    const availableWidth = containerWidth - reservedWidth - 27; // 추가 여유공간을 20px로 조정
    
    devLog('Mobile chip calculation:', {
      containerWidth,
      reservedWidth,
      availableWidth,
      dropdownWidth,
      moreButtonWidth
    });
    
    // 각 칩의 실제 너비를 더 보수적으로 계산
    const chips = getFilterChips(lang);
    let usedWidth = 0;
    let maxChips = 0;
    
    for (let i = 0; i < chips.length; i++) {
      // 더 보수적인 칩 너비 계산
      const textLength = chips[i].label.length;
      const fontSize = 14;
      // 텍스트 너비를 조금 덜 보수적으로 계산 (75%로 조정)
      const estimatedTextWidth = textLength * (fontSize * 0.75);
      const chipWidth = estimatedTextWidth + 34; // 좌우 패딩을 34px로 조정
      
      // 칩들 사이의 갭도 고려
      const totalWidthWithCurrentChip = usedWidth + chipWidth + (i > 0 ? gap : 0);
      
      devLog(`Chip ${i} (${chips[i].label}):`, {
        textLength,
        estimatedTextWidth,
        chipWidth,
        totalWidthWithCurrentChip,
        availableWidth
      });
      
      if (totalWidthWithCurrentChip > availableWidth) {
        // 첫 번째 칩도 들어가지 않으면 최소 1개는 보여주기
        if (maxChips === 0) {
          maxChips = 1;
        }
        break;
      }
      
      usedWidth = totalWidthWithCurrentChip;
      maxChips = i + 1;
    }
    
    devLog('Final maxChips:', maxChips);
    return Math.max(1, maxChips);
  }, [lang]);

  // 윈도우 리사이즈 및 초기 로드 시 최대 칩 개수 계산
  useEffect(() => {
    const updateMaxChips = () => {
      const maxChips = calculateMaxVisibleChips();
      setMaxVisibleChips(maxChips);
    };
    
    // 초기 계산 (DOM이 완전히 렌더링된 후)
    const timer = setTimeout(updateMaxChips, 200);
    
    // 윈도우 리사이즈 시 재계산
    const handleResize = () => {
      setTimeout(updateMaxChips, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateMaxVisibleChips]);

  // 커스텀 필터 칩들 렌더링 함수
  const renderCustomFilterChips = () => {
    const chips = getFilterChips(lang);
    const isWeb = window.innerWidth >= 768;
    
    if (isWeb) {
      // 웹: 9개까지만 첫 줄에, 나머지는 다음 줄에
      const firstRowChips = chips.slice(0, 9);
      const remainingChips = chips.slice(9);
      const hasMoreChips = remainingChips.length > 0;
      
      // 칩들만 렌더링
      const chipElements = [
        ...firstRowChips.map((chip) => {
          const isActive = selectedChips.includes(chip.key);
          
          return (
            <CustomChipButton
              key={chip.key}
              $isActive={isActive}
              onClick={() => handleChipClick(chip.key)}
            >
              {chip.label}
            </CustomChipButton>
          );
        }),
        // 더보기 버튼 추가 (칩들과 같은 레벨)
        ...(hasMoreChips ? [
          <MoreButton
            key="toggle-button"
            $isExpanded={isChipsExpanded}
            onClick={toggleChipsExpansion}
          />
        ] : []),
        // 확장 시 나머지 칩들 추가
        ...(isChipsExpanded && hasMoreChips ? remainingChips.map((chip) => {
          const isActive = selectedChips.includes(chip.key);
          
          return (
            <CustomChipButton
              key={chip.key}
              $isActive={isActive}
              onClick={() => handleChipClick(chip.key)}
            >
              {chip.label}
            </CustomChipButton>
          );
        }) : [])
      ];
      
      return chipElements;
    } else {
      // 모바일: 더보기 버튼을 항상 오른쪽 첫 줄에 고정
      const hasMoreChips = chips.length > maxVisibleChips;
      
      // 첫 줄과 나머지 줄 분리
      const firstRowChips = chips.slice(0, maxVisibleChips);
      const remainingChips = chips.slice(maxVisibleChips);
      
      const chipElements = [
        // 첫 번째 줄 칩들
        ...firstRowChips.map((chip) => {
          const isActive = selectedChips.includes(chip.key);
          
          return (
            <CustomChipButton
              key={chip.key}
              $isActive={isActive}
              onClick={() => handleChipClick(chip.key)}
            >
              {chip.label}
            </CustomChipButton>
          );
        }),
        // 더보기 버튼 추가 (칩들과 같은 레벨)
        ...(hasMoreChips ? [
          <MoreButton
            key="toggle-button"
            $isExpanded={isChipsExpanded}
            onClick={toggleChipsExpansion}
          />
        ] : []),
        // 확장 시 나머지 칩들
        ...(isChipsExpanded ? remainingChips.map((chip) => {
          const isActive = selectedChips.includes(chip.key);
          
          return (
            <CustomChipButton
              key={chip.key}
              $isActive={isActive}
              onClick={() => handleChipClick(chip.key)}
            >
              {chip.label}
            </CustomChipButton>
          );
        }) : [])
      ];

      return chipElements;
    }
  };

  // 즐겨찾기 토글 핸들러 (현재 사용하지 않음 - 향후 구현 예정)
  // const handleFavoriteToggle = async (e: React.MouseEvent, lotId: number, currentLikedUserIndexes?: number[]) => {
  //   e.stopPropagation();
    
  //   if (!user?.index) {
  //     navigate('/login');
  //     return;
  //   }

  //   try {
  //     // TODO: 실제 API 호출 구현
  //     devLog('즐겨찾기 토글:', { lotId, currentLikedUserIndexes, userIndex: user.index });
      
  //     const wasLiked = currentLikedUserIndexes ? currentLikedUserIndexes.includes(user.index) : false;
      
  //     let newLikedUserIndexes: number[] = [];
  //     if (wasLiked) {
  //       newLikedUserIndexes = currentLikedUserIndexes ? currentLikedUserIndexes.filter(index => index !== user.index) : [];
  //     } else {
  //       newLikedUserIndexes = currentLikedUserIndexes ? [...currentLikedUserIndexes, user.index] : [user.index];
  //     }
      
  //     setSearchResults(prev => 
  //       prev.map(item => 
  //         item.id === lotId
  //           ? { ...item, likedUserIndexes: newLikedUserIndexes }
  //           : item
  //       )
  //     );
      
  //   } catch (error) {
  //     devError('즐겨찾기 처리 중 오류:', error);
  //   }
  // };

  // 카드 클릭 핸들러
  const handleCardClick = (item: SearchResultItem) => {
    const auctionStatus = getAuctionStatus(item);
    const auction = item.auction;
    
    // 쇼핑 모드일 때는 활성 상태에서만 ShoppingRequest로 이동
    if (isShoppingMode()) {
      if (auctionStatus === 'submission-period' || auctionStatus === 'live') {
        navigate(`/shopping-request/${item.index}`);
        return;
      } else if (auctionStatus === 'before-submission' || auctionStatus === 'before-live') {
        return; // 아무것도 하지 않음
      }
    }
    
    if (auctionStatus === 'before-submission') {
      const preBidStartTime = new Date(auction!.preBidStartAt);
      const startTimeMessage = formatDateToLocalString(preBidStartTime, lang);
      setModalState({
        isOpen: true,
        title: texts.auctionNotStarted,
        message: lang === 'ko' 
          ? `${startTimeMessage}부터 사전입찰이 시작됩니다`
          : `Pre-bidding starts from ${startTimeMessage}`,
        buttonText: texts.confirmButton
      });
    } else if (auctionStatus === 'before-live') {
      const auctionStartTime = new Date(auction!.auctionStartAt);
      const startTimeMessage = formatDateToLocalString(auctionStartTime, lang);
      setModalState({
        isOpen: true,
        title: lang === 'ko' ? '라이브 경매 시작전입니다' : 'Live Auction Not Started',
        message: lang === 'ko' 
          ? `${startTimeMessage}부터 라이브 경매가 시작됩니다` 
          : `Live auction starts from ${startTimeMessage}`,
        buttonText: texts.confirmButton
      });
    } else if (auctionStatus === 'ended') {
      setModalState({
        isOpen: true,
        title: texts.auctionEndedTitle,
        message: texts.auctionEndedMessage,
        buttonText: texts.confirmButton
      });
    } else if (auctionStatus === 'submission-period') {
      const lotId = item.index;
      navigateWithScrollTop(navigate, `/pre-bid/${lotId}`, { state: { selectedItem: item } });
    } else if (auctionStatus === 'live') {
      // LiveBid 페이지에서 필요한 정보를 포함한 객체로 변환하여 전달
      const transformedItem = {
        ...item,
        id: item.index, // lotIndex로 사용될 값
        auctionIndex: item.auction?.index, // auctionIndex 명시적 추가
        lotIndex: item.index, // lotIndex 명시적 추가
        auctionId: item.auction?.index, // 호환성을 위한 추가
        lotId: item.index, // 호환성을 위한 추가
        apiData: {
          index: item.index,
          auction: item.auction
        }
      };
      navigateWithScrollTop(navigate, '/live-bid', { state: { selectedItem: transformedItem, auctionIndex: item.auction?.index, lotIndex: item.index } });
    }
  };

  // 모바일 검색 핸들러들
  const handleMobileSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMobileSearchQuery(e.target.value);
  };

  const handleMobileSearchSubmit = () => {
    if (mobileSearchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(mobileSearchQuery.trim())}`);
    } else {
      navigate('/search');
    }
  };

  const handleMobileSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleMobileSearchSubmit();
    }
  };

  // 모달 닫기 함수
  const closeModal = () => {
    setModalState({
      isOpen: false,
      title: '',
      message: '',
      buttonText: ''
    });
  };

  // 검색 결과 카드 렌더링 - 새로운 인터페이스에 맞게 수정
  const renderSearchCard = (item: SearchResultItem) => {
    const auctionStatus = getAuctionStatus(item);
    
    // 사용자가 해당 LOT을 좋아요했는지 확인하는 함수
    const isLikedByUser = (likedUserIndexes: number[] | null) => {
      if (!user?.index || !likedUserIndexes || !Array.isArray(likedUserIndexes)) {
        return false;
      }
      return likedUserIndexes.includes(user.index);
    };

    // 즐겨찾기 클릭 핸들러
    const handleFavoriteClick = async (e: React.MouseEvent) => {
      e.stopPropagation();
      // TODO: 향후 즐겨찾기 기능 구현
      devLog('즐겨찾기 클릭:', item.index);
    };

    // 이미지 URL 생성
    const imageUrl = item.images.length > 0 ? 
      (item.images[0].startsWith('http') ? 
        item.images[0] : 
        `${process.env.REACT_APP_API_HOST || 'http://localhost:3001'}/${item.images[0]}`) : 
      '/common/nodata.svg';

    // 경매 종료 여부 확인
    const isEnded = auctionStatus === 'ended' || item.ended;

    // 사전입찰 시간 (auction의 preBidStartAt 사용)
    const preBidStartTime = item.auction?.preBidStartAt ? new Date(item.auction.preBidStartAt) : null;
    const now = new Date();
    const shouldShowOverlay = preBidStartTime ? preBidStartTime > now : false;

    return (
      <AuctionCard key={item.index} onClick={() => handleCardClick(item)}>
        {/* 종료된 경매가 아닐 때만 라이브 뱃지 표시 (쇼핑 모드에서는 숨김) */}
        {!isEnded && item.auction && !isShoppingMode() && (
          <BadgeContainer>
            <LiveBadge 
              liveTime={formatLiveTime(item.auction.auctionStartAt, lang, item.auction.auctionEndAt)} 
              isMobile={true}
            />
          </BadgeContainer>
        )}
        
        <CardImageContainer>
          <ImageView
            imageUrl={imageUrl}
            width="100%"
            height="100%"
            borderRadius={0}
            alt={item.name}
            objectFit="contain"
          />
          
          {/* 종료된 경매일 때 전체 반투명 오버레이 */}
          {isEnded && (
            <EndedAuctionOverlay>
              <EndedAuctionText>{texts.auctionEnded}</EndedAuctionText>
            </EndedAuctionOverlay>
          )}
          
          {/* 좋아요 아이콘 (이미지 안의 오른쪽 상단) */}
          <FavoriteIcon 
            isFavorite={isLikedByUser(item.liked_user_indexes || null)} 
            onClick={handleFavoriteClick}
          />
          
          {/* 이미지 하단 오버레이 - 사전입찰 시간이 현재 시간 이후이고 종료되지 않은 경매일 때만 표시 (쇼핑 모드에서는 숨김) */}
          {shouldShowOverlay && !isEnded && item.auction && !isShoppingMode() && (
            <ImageOverlay>
              <ResponsiveView
                mobileView={
                  <>
                    <OverlayDateInfo>
                      <OverlayDateLabel>{texts.preBid}</OverlayDateLabel>
                      <OverlayDateValue>{formatAuctionPeriod(item.auction.preBidStartAt, item.auction.preBidEndAt).split(' ~ ')[0]}</OverlayDateValue>
                    </OverlayDateInfo>
                    
                    <OverlayDateInfo>
                      <OverlayDateLabel>{texts.auctionStart}</OverlayDateLabel>
                      <OverlayDateValue>{formatAuctionPeriod(item.auction.auctionStartAt, item.auction.auctionEndAt).split(' ~ ')[0]}</OverlayDateValue>
                    </OverlayDateInfo>
                    
                    <OverlayDateInfo>
                      <OverlayDateLabel>{texts.auctionEnd}</OverlayDateLabel>
                      <OverlayDateValue>{formatAuctionPeriod(item.auction.auctionStartAt, item.auction.auctionEndAt).split(' ~ ')[1]}</OverlayDateValue>
                    </OverlayDateInfo>
                  </>
                }
                desktopView={
                  <>
                    <DesktopOverlayDateInfo>
                      <DesktopOverlayDateLabel>{texts.preBid}</DesktopOverlayDateLabel>
                      <DesktopOverlayDateValue>{formatAuctionPeriod(item.auction.preBidStartAt, item.auction.preBidEndAt)}</DesktopOverlayDateValue>
                    </DesktopOverlayDateInfo>
                    
                    <DesktopOverlayDateInfo>
                      <DesktopOverlayDateLabel>{isShoppingMode() ? texts.auctionStart + ' ~ ' + texts.auctionEnd : '경매 일시'}</DesktopOverlayDateLabel>
                      <DesktopOverlayDateValue>{formatAuctionPeriod(item.auction.auctionStartAt, item.auction.auctionEndAt)}</DesktopOverlayDateValue>
                    </DesktopOverlayDateInfo>
                  </>
                }
              />
            </ImageOverlay>
          )}
        </CardImageContainer>
        
        <CardContent>
          <AuctionHouseComponent 
            auctioneerName={item.auctioneer.name}
            auctioneerIndex={item.auctioneer.index}
          />
          <AuctionTitle>{item.name}</AuctionTitle>
          {!isShoppingMode() && (
            <LotName>{item.code}</LotName>
          )}
          
          {/* 가격 정보 */}
          <PriceInfo>
            <PriceItem>
              <PriceLabel>{texts.startPrice}</PriceLabel>
              <PriceValue>
                {formatPrice(item.start_price, lang, item.exchange_rate)}
              </PriceValue>
            </PriceItem>
            {/* 최고 사전입찰가 표시 (종료되지 않은 경매) */}
            {!isEnded && item.max_pre_bid_price > 0 && (
              <PriceItem>
                <PriceLabel>{texts.maxPreBidPrice}</PriceLabel>
                <PriceValue>
                  {formatPrice(item.max_pre_bid_price, lang, item.exchange_rate)}
                </PriceValue>
              </PriceItem>
            )}
          </PriceInfo>
        </CardContent>
      </AuctionCard>
    );
  };

  // 모바일 검색창 렌더링
  const renderMobileSearchInput = () => (
    <MobileSearchContainer>
      <MobileSearchInputWrapper>
        <MobileSearchInputField
          type="text"
          placeholder={texts.searchPlaceholder}
          value={mobileSearchQuery}
          onChange={handleMobileSearchChange}
          onKeyPress={handleMobileSearchKeyPress}
        />
        <MobileSearchIcon onClick={handleMobileSearchSubmit} />
      </MobileSearchInputWrapper>
    </MobileSearchContainer>
  );

  const renderContent = () => {
    return (
      <>
        {/* 검색 결과 헤더 - 검색어가 있을 때만 표시 */}
        {query && (
          <SearchResultsHeader>
            <SearchQuery>"{query}" {texts.searchResults}</SearchQuery>
            <SearchCount>{searchResults.length}{texts.resultsCount}</SearchCount>
          </SearchResultsHeader>
        )}
        
        {/* 필터 칩은 항상 표시 */}
        <FilterContainer ref={filterContainerRef}>
          {/* 쇼핑 모드일 때는 경매 상태 드롭다운 숨김 */}
          {!isShoppingMode() && (
            <AuctionStatusDropdownContainer>
              <AppDropdownField
                value={selectedAuctionStatus}
                onChange={handleAuctionStatusChange}
                options={getAuctionStatusOptions(lang)}
                height="40px"
                radius="8px"
                padding="12px"
                borderColor="#ddd"
                focusBorderColor="#007bff"
                background="#fff"
              />
            </AuctionStatusDropdownContainer>
          )}
          {/* 쇼핑 모드에서는 카테고리 필터 칩스 숨김 */}
          {!isShoppingMode() && (
            <FilterChipsContainer>
              {renderCustomFilterChips()}
            </FilterChipsContainer>
          )}
        </FilterContainer>

        {/* 검색 결과가 없는 경우 */}
        {!isLoading && query && searchResults.length === 0 && (
          <SearchResultsGrid style={{ 
            gridTemplateColumns: '1fr', 
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <NoDataContainer 
              message={texts.noResults}
            />
          </SearchResultsGrid>
        )}

        {/* 검색어가 없는 경우 */}
        {!isLoading && !query && (
          <SearchResultsGrid style={{ 
            gridTemplateColumns: '1fr', 
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <NoDataContainer 
              message={texts.noSearchPrompt}
            />
          </SearchResultsGrid>
        )}

        {/* 로딩 상태 */}
        {isLoading && (
          <SearchResultsGrid style={{ 
            gridTemplateColumns: '1fr', 
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <NoDataContainer 
              message="검색 중..."
            />
          </SearchResultsGrid>
        )}

        {/* 검색 결과 표시 */}
        {!isLoading && searchResults.length > 0 && (
          <SearchResultsGrid>
            {searchResults.map(renderSearchCard)}
          </SearchResultsGrid>
        )}
      </>
    );
  };

  const renderDesktopContent = () => {
    return (
      <>
        {/* 데스크톱용 타이틀 - 검색어가 있을 때만 검색 결과 개수 표시 */}
        <FormTitle>
          {query 
            ? `"${query}" ${texts.searchResults} (${searchResults.length})` 
            : texts.title
          }
        </FormTitle>
        
        {/* 필터 칩은 항상 표시 */}
        <FilterContainer ref={filterContainerRef}>
          {/* 쇼핑 모드일 때는 경매 상태 드롭다운 숨김 */}
          {!isShoppingMode() && (
            <AuctionStatusDropdownContainer>
              <AppDropdownField
                value={selectedAuctionStatus}
                onChange={handleAuctionStatusChange}
                options={getAuctionStatusOptions(lang)}
                height="40px"
                radius="8px"
                padding="12px"
                borderColor="#ddd"
                focusBorderColor="#007bff"
                background="#fff"
              />
            </AuctionStatusDropdownContainer>
          )}
          {/* 쇼핑 모드에서는 카테고리 필터 칩스 숨김 */}
          {!isShoppingMode() && (
            <FilterChipsContainer>
              {renderCustomFilterChips()}
            </FilterChipsContainer>
          )}
        </FilterContainer>

        {/* 검색 결과가 없는 경우 */}
        {!isLoading && query && searchResults.length === 0 && (
          <SearchResultsGrid style={{ 
            gridTemplateColumns: '1fr', 
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <NoDataContainer 
              message={texts.noResults}
            />
          </SearchResultsGrid>
        )}

        {/* 검색어가 없는 경우 */}
        {!isLoading && !query && (
          <SearchResultsGrid style={{ 
            gridTemplateColumns: '1fr', 
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <NoDataContainer 
              message={texts.noSearchPrompt}
            />
          </SearchResultsGrid>
        )}

        {/* 로딩 상태 */}
        {isLoading && (
          <SearchResultsGrid style={{ 
            gridTemplateColumns: '1fr', 
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <NoDataContainer 
              message="검색 중..."
            />
          </SearchResultsGrid>
        )}

        {/* 검색 결과 표시 */}
        {!isLoading && searchResults.length > 0 && (
          <SearchResultsGrid>
            {searchResults.map(renderSearchCard)}
          </SearchResultsGrid>
        )}
      </>
    );
  };

  // 모바일 레이아웃
  const MobileLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          showMobileSearch={false}
          onBack={() => navigate(-1)}
        />
        {renderMobileSearchInput()}
        <SearchPageContainer>
          <ContentContainer>
            {renderContent()}
          </ContentContainer>
        </SearchPageContainer>
        
        <Modal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          header={modalState.title}
          body={modalState.message}
          footer={
            <PrimaryButton onClick={closeModal}>
              {modalState.buttonText}
            </PrimaryButton>
          }
        />
      </CenteredLayout>
    </MainLayout>
  );

  // 데스크톱 레이아웃
  const DesktopLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <SearchPageContainer>
          <ContentContainer>
            {renderDesktopContent()}
          </ContentContainer>
        </SearchPageContainer>
        
        <Modal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          header={modalState.title}
          body={modalState.message}
          footer={
            <PrimaryButton onClick={closeModal}>
              {modalState.buttonText}
            </PrimaryButton>
          }
        />
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={MobileLayout}
      desktopView={DesktopLayout}
    />
  );
}

export default SearchPage;
